'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  HelpCircle, 
  BookOpen, 
  MessageCircle, 
  Wrench, 
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  MessageSquare,
  ExternalLink,
  ArrowRight
} from 'lucide-react';
import PageTransition from '@/components/PageTransition';
import PageHero from '@/components/layout/PageHero';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: 'general' | 'account' | 'jobs' | 'community';
}

const FAQ_DATA: FAQItem[] = [
  {
    id: '1',
    question: 'How do I create an account?',
    answer: 'You can create an account by clicking the "Sign Up" button and choosing between a talent or scout account. You can also sign up using your Google account for a faster process.',
    category: 'account'
  },
  {
    id: '2',
    question: 'What\'s the difference between talent and scout accounts?',
    answer: 'Talent accounts are for creative professionals looking for opportunities, while scout accounts are for employers and recruiters looking to hire talent. Scouts can post jobs and connect with talent.',
    category: 'account'
  },
  {
    id: '3',
    question: 'How do I apply for jobs?',
    answer: 'Browse available jobs in the Jobs section, click on a job that interests you, and use the "Apply" button to submit your application with your portfolio and resume.',
    category: 'jobs'
  },
  {
    id: '4',
    question: 'How do I post a job as a scout?',
    answer: 'Scouts need to be approved first. Once approved, you can post jobs from your Scout Dashboard. Make sure to provide detailed requirements and compensation information.',
    category: 'jobs'
  },
  {
    id: '5',
    question: 'How do I connect with other users?',
    answer: 'Visit user profiles and use the "Connect" button to send a connection request. You can also join groups and participate in community discussions.',
    category: 'community'
  },
  {
    id: '6',
    question: 'How do I upload my portfolio?',
    answer: 'Go to your profile settings and use the media upload section to add images, audio files, videos, and documents to showcase your work.',
    category: 'general'
  },
  {
    id: '7',
    question: 'What file types are supported for uploads?',
    answer: 'We support images (JPG, PNG, GIF), audio files (MP3, WAV, M4A), video files (MP4, MOV, AVI), and documents (PDF, DOC, DOCX).',
    category: 'general'
  },
  {
    id: '8',
    question: 'How do I get verified as a scout?',
    answer: 'Submit your professional information including company details and industry experience. Our team will review your application within 2-3 business days.',
    category: 'account'
  }
];

const GETTING_STARTED_STEPS = [
  {
    title: 'Create Your Account',
    description: 'Sign up as either talent or scout and complete your profile',
    icon: '👤'
  },
  {
    title: 'Complete Your Profile',
    description: 'Add your bio, specialties, and upload your portfolio',
    icon: '📝'
  },
  {
    title: 'Connect with Others',
    description: 'Browse profiles and send connection requests',
    icon: '🤝'
  },
  {
    title: 'Find Opportunities',
    description: 'Apply for jobs or post opportunities as a scout',
    icon: '💼'
  }
];

export default function HelpCenterPage() {
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'general' | 'account' | 'jobs' | 'community'>('all');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const filteredFAQs = FAQ_DATA.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleFAQToggle = (id: string) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle contact form submission
    console.log('Contact form submitted:', contactForm);
    // Reset form
    setContactForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <PageTransition>
      <div data-theme="booth" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        <PageHero
          title="Help Center"
          subtitle="Find answers to your questions and get support"
          variant="inline"
          background="none"
        />
        <div className="container mx-auto px-4 pb-8">

          <div className="max-w-6xl mx-auto">
            {/* Search */}
            <div className="mb-8">
              <div className="relative max-w-2xl mx-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  placeholder="Search for help articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                />
              </div>
            </div>

            {/* Getting Started */}
            <Card className="bg-slate-800/50 border-slate-700 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center space-x-2">
                  <BookOpen className="w-6 h-6 text-blue-400" />
                  <span>Getting Started</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {GETTING_STARTED_STEPS.map((step, index) => (
                    <div key={index} className="text-center">
                      <div className="text-4xl mb-3">{step.icon}</div>
                      <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                      <p className="text-slate-400 text-sm">{step.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* FAQs */}
            <Card className="bg-slate-800/50 border-slate-700 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center space-x-2">
                  <HelpCircle className="w-6 h-6 text-green-400" />
                  <span>Frequently Asked Questions</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Category Filter */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {[
                    { key: 'all', label: 'All', color: 'bg-blue-600' },
                    { key: 'general', label: 'General', color: 'bg-purple-600' },
                    { key: 'account', label: 'Account', color: 'bg-green-600' },
                    { key: 'jobs', label: 'Jobs', color: 'bg-orange-600' },
                    { key: 'community', label: 'Community', color: 'bg-pink-600' }
                  ].map((category) => (
                    <Button
                      key={category.key}
                      variant={selectedCategory === category.key ? 'default' : 'outline'}
                      onClick={() => setSelectedCategory(category.key as 'all' | 'general' | 'account' | 'jobs' | 'community')}
                      className={selectedCategory === category.key ? category.color : ''}
                      size="sm"
                    >
                      {category.label}
                    </Button>
                  ))}
                </div>

                {/* FAQ List */}
                <div className="space-y-4">
                  {filteredFAQs.map((faq) => (
                    <div key={faq.id} className="border border-slate-700 rounded-lg">
                      <button
                        onClick={() => handleFAQToggle(faq.id)}
                        className="w-full p-4 text-left flex items-center justify-between hover:bg-slate-700/50 transition-colors"
                      >
                        <span className="text-white font-medium">{faq.question}</span>
                        {expandedFAQ === faq.id ? (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      {expandedFAQ === faq.id && (
                        <div className="px-4 pb-4">
                          <p className="text-slate-300">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Contact Support */}
            <Card className="bg-slate-800/50 border-slate-700 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center space-x-2">
                  <MessageCircle className="w-6 h-6 text-yellow-400" />
                  <span>Contact Support</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Contact Form */}
                  <div>
                    <h3 className="text-white font-semibold mb-4">Send us a message</h3>
                    <form onSubmit={handleContactSubmit} className="space-y-4">
                      <Input
                        placeholder="Your name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                        required
                      />
                      <Input
                        type="email"
                        placeholder="Your email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                        required
                      />
                      <Input
                        placeholder="Subject"
                        value={contactForm.subject}
                        onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                        required
                      />
                      <Textarea
                        placeholder="Your message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white min-h-[120px]"
                        required
                      />
                      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                        Send Message
                      </Button>
                    </form>
                  </div>

                  {/* Contact Info */}
                  <div>
                    <h3 className="text-white font-semibold mb-4">Other ways to reach us</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-blue-400" />
                        <div>
                          <p className="text-white font-medium">Email Support</p>
                          <p className="text-slate-400 text-sm">support@indiestry.com</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MessageSquare className="w-5 h-5 text-green-400" />
                        <div>
                          <p className="text-white font-medium">Live Chat</p>
                          <p className="text-slate-400 text-sm">Available 24/7</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-purple-400" />
                        <div>
                          <p className="text-white font-medium">Phone Support</p>
                          <p className="text-slate-400 text-sm">+1 (555) 123-4567</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 p-4 bg-slate-700/50 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Response Time</h4>
                      <p className="text-slate-400 text-sm">
                        We typically respond to support requests within 24 hours during business days.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Troubleshooting */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center space-x-2">
                  <Wrench className="w-6 h-6 text-red-400" />
                  <span>Troubleshooting</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-white font-semibold mb-3">Common Issues</h3>
                    <ul className="space-y-2 text-slate-300">
                      <li>• Can&apos;t upload files? Check file size and format</li>
                      <li>• Login issues? Try resetting your password</li>
                      <li>• Profile not updating? Clear your browser cache</li>
                      <li>• Messages not sending? Check your internet connection</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-3">Quick Fixes</h3>
                    <ul className="space-y-2 text-slate-300">
                      <li>• Refresh the page if something isn&apos;t loading</li>
                      <li>• Try a different browser if you&apos;re having issues</li>
                      <li>• Check your email for verification links</li>
                      <li>• Update your browser to the latest version</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PageTransition>
  );
} 